/**
 * Typed wrappers around the ``/admin/*`` endpoints.
 *
 * Every function is gated server-side by the
 * ``require_internal_super_admin`` dep (SUPER_ADMIN × is_internal
 * org). The SPA assumes the auth gate has run before any of these
 * fire — if a customer somehow lands here, the 403 redirect in
 * ``client.ts`` handles bailing out.
 */
import client from "./client";

// ── Auth / identity ─────────────────────────────────────────────

export interface MeResponse {
  id: string;
  email: string;
  role: string;
  org_id: string;
  /** Org-level fields the SPA needs to enforce the is_internal gate. */
  org?: {
    id: string;
    name: string;
    is_internal: boolean;
  };
}

/** Fetch the current user. Used by the auth gate. */
export async function getMe(): Promise<MeResponse> {
  const res = await client.get<MeResponse>("/auth/me");
  return res.data;
}

// ── Overview ────────────────────────────────────────────────────

export interface OverviewStats {
  total_mrr_usd: number;
  active_customers: number;
  founding_spots_remaining: number;
  total_employees: number;
  api_spend_today_usd: number;
  active_users_today: number;
}

export interface DailySignup {
  date: string;
  count: number;
}

export interface DailyMrr {
  date: string;
  mrr_usd: number;
}

export interface ActivityEntry {
  type: "signup" | "subscription" | "credit_purchase" | string;
  org_id: string;
  org_name: string;
  label: string;
  at: string | null;
}

export interface OverviewResponse {
  stats: OverviewStats;
  signups_30d: DailySignup[];
  mrr_30d: DailyMrr[];
  recent_activity: ActivityEntry[];
}

export async function getOverview(): Promise<OverviewResponse> {
  const res = await client.get<OverviewResponse>("/admin/overview");
  return res.data;
}

// ── Newsletter subscribers ───────────────────────────────────────

export interface NewsletterSubscriber {
  id: string;
  email: string;
  source: string | null;
  is_active: boolean;
  subscribed_at: string | null;
  unsubscribed_at: string | null;
}

export interface NewsletterResponse {
  total_active: number;
  total_lifetime: number;
  subscribers: NewsletterSubscriber[];
}

export async function getNewsletter(): Promise<NewsletterResponse> {
  const res = await client.get<NewsletterResponse>("/admin/newsletter");
  return res.data;
}

export interface NewsletterSendResult {
  sent: number;
  failed: number;
  recipients: number;
  test: boolean;
}

/** Compose + send a newsletter. Pass ``test_email`` to send only to that
 *  address (a preview) without touching the subscriber list. */
export async function sendNewsletter(payload: {
  subject: string;
  body: string;
  test_email?: string;
}): Promise<NewsletterSendResult> {
  const res = await client.post<NewsletterSendResult>(
    "/admin/newsletter/send",
    payload,
  );
  return res.data;
}

/** Render the exact branded email HTML for the composer's live preview
 *  (same template the send uses). Returns the full HTML document. */
export async function previewNewsletter(payload: {
  subject: string;
  body: string;
}): Promise<{ html: string }> {
  const res = await client.post<{ html: string }>(
    "/admin/newsletter/preview",
    payload,
  );
  return res.data;
}

// ── Customers ───────────────────────────────────────────────────

export interface CustomerRow {
  id: string;
  name: string;
  plan: string;
  employees: number;
  monthly_revenue_usd: number;
  credits_used: number;
  credits_allowance: number;
  purchased_credits: number;
  last_active_at: string | null;
  created_at: string | null;
  status: string;
}

export async function listCustomers(): Promise<CustomerRow[]> {
  const res = await client.get<CustomerRow[]>("/admin/customers");
  return res.data;
}

export interface CustomerUser {
  id: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login: string | null;
}

export interface CustomerCommand {
  id: string;
  command_text: string;
  intent_type: string;
  status: string;
  created_at: string | null;
}

export interface CustomerCreditTxn {
  id: string;
  type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  created_at: string | null;
}

export interface CustomerSubEvent {
  id: string;
  event_type: string;
  created_at: string | null;
}

export interface CustomerDetail {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  subscription_status: string | null;
  trial_ends_at: string | null;
  created_at: string | null;
  employees: number;
  monthly_revenue_usd: number;
  signup_source: string | null;
  domain: string | null;
  internal_notes: string;
  users: CustomerUser[];
  credits: {
    monthly_allowance: number;
    monthly_used: number;
    purchased_balance: number;
    last_reset_at: string | null;
  } | null;
  recent_commands: CustomerCommand[];
  recent_credit_transactions: CustomerCreditTxn[];
  subscription_history: CustomerSubEvent[];
}

export async function getCustomer(orgId: string): Promise<CustomerDetail> {
  const res = await client.get<CustomerDetail>(`/admin/customers/${orgId}`);
  return res.data;
}

export async function saveCustomerNotes(
  orgId: string,
  notes: string,
): Promise<void> {
  await client.post(`/admin/customers/${orgId}/notes`, { notes });
}

// ── AI Usage ────────────────────────────────────────────────────

export interface AiUsageRow {
  org_id: string;
  name: string;
  plan: string;
  commands_today: number;
  commands_week: number;
  commands_month: number;
  credits_consumed: number;
  estimated_cost_usd: number;
  alert_high_spend: boolean;
  top_commands: { intent: string; count: number }[];
  tier_breakdown: { haiku: number; sonnet: number; skills: number };
}

export interface DailyCostPoint {
  date: string;
  cost_usd: number;
  commands: number;
  cost_per_command_usd: number;
}

export interface AiUsageResponse {
  per_org: AiUsageRow[];
  daily_cost: DailyCostPoint[];
}

export async function getAiUsage(): Promise<AiUsageResponse> {
  const res = await client.get<AiUsageResponse>("/admin/ai-usage");
  return res.data;
}

// ── Revenue ─────────────────────────────────────────────────────

export interface RevenueByPlan {
  plan: string;
  customers: number;
  mrr_usd: number;
}

export interface RevenueResponse {
  total_mrr_usd: number;
  active_customers: number;
  arpu_usd: number;
  by_plan: RevenueByPlan[];
  mrr_30d: DailyMrr[];
}

export async function getRevenue(): Promise<RevenueResponse> {
  const res = await client.get<RevenueResponse>("/admin/revenue");
  return res.data;
}

// ── DevOps ──────────────────────────────────────────────────────

export interface DbHealth {
  status: "healthy" | "down" | "unavailable" | string;
  latency_ms: number | null;
  error?: string;
  pool: {
    checked_out?: number;
    size?: number;
    overflow?: number;
    checkedin?: number;
  };
}

export interface RedisHealth {
  status: "healthy" | "down" | "disabled" | "unavailable" | string;
  latency_ms: number | null;
  error?: string;
}

export interface ServicesHealth {
  api: { status: string };
  database: DbHealth;
  redis: RedisHealth;
  timestamp: string;
}

export interface SystemInfo {
  python_version: string;
  platform: string;
  hostname: string;
  app_version: string;
  uptime_seconds: number;
  process_start: string;
  environment: string;
  render_service_id: string;
  render_instance_id: string;
  database_host_masked: string;
  alembic_version: string | null;
  redis_configured: boolean;
}

export interface DeployInfo {
  current_commit: string | null;
  current_commit_short: string | null;
  github_url: string | null;
  alembic_version: string | null;
  process_start: string;
  uptime_seconds: number;
  environment: string;
  last_deploy_status: string;
}

export interface ErrorRow {
  id: string;
  timestamp: string | null;
  method: string | null;
  path: string | null;
  status_code: number | null;
  error_type: string | null;
  error_message: string;
  traceback: string | null;
  user_id: string | null;
  org_id: string | null;
  request_id: string | null;
}

export interface JobRunRow {
  id: string;
  started_at: string | null;
  finished_at: string | null;
  status: "running" | "success" | "error" | string;
  result: string | null;
  error: string | null;
}

export interface JobBlock {
  name: string;
  schedule: string;
  runs: JobRunRow[];
}

export interface MetricsResponse {
  avg_ai_duration_ms: number;
  p95_ai_duration_ms: number;
  ai_samples_1h: number;
  errors_1h: number;
  errors_24h: number;
  logins_24h: number;
  error_rate_24h_pct: number | null;
}

export async function getServicesHealth(): Promise<ServicesHealth> {
  const res = await client.get<ServicesHealth>("/admin/health/services");
  return res.data;
}

export async function getSystemInfo(): Promise<SystemInfo> {
  const res = await client.get<SystemInfo>("/admin/system/info");
  return res.data;
}

export async function getDeployInfo(): Promise<DeployInfo> {
  const res = await client.get<DeployInfo>("/admin/deploys");
  return res.data;
}

export async function getErrors(
  hours = 24,
  limit = 50,
): Promise<ErrorRow[]> {
  const res = await client.get<ErrorRow[]>("/admin/errors", {
    params: { hours, limit },
  });
  return res.data;
}

export async function getJobs(): Promise<JobBlock[]> {
  const res = await client.get<JobBlock[]>("/admin/jobs");
  return res.data;
}

export async function getMetrics(): Promise<MetricsResponse> {
  const res = await client.get<MetricsResponse>("/admin/metrics");
  return res.data;
}

export async function actionClearCache(): Promise<{
  ok: boolean;
  cleared?: number;
  error?: string;
}> {
  const res = await client.post("/admin/actions/clear-cache");
  return res.data;
}

export async function actionRunSentinel(): Promise<{
  ok: boolean;
  result?: unknown;
  error?: string;
}> {
  const res = await client.post("/admin/actions/run-sentinel");
  return res.data;
}
